import { useEffect, useState } from "react";

/* Is the on-screen keyboard covering the bottom of the app?

   No browser answers that directly, so this reads the signals that imply it.
   The order matters: the cheap, exact signal is tried first, and measurement
   is only the fallback for devices the cheap one doesn't cover.

   1. A text-entry element has focus, on a device whose primary pointer is
      coarse. On a phone that *is* the answer — a focused field means the
      keyboard is up, and nothing in this app focuses a field without a user
      gesture (iOS doesn't raise the keyboard for a programmatic focus
      anyway), so there is no case where this is focused-but-closed.

   2. Otherwise, a viewport that got shorter than it was when focus arrived —
      the desktop and hardware-keyboard case, where a focused field means
      nothing and only an actual shrink does. Both viewports are watched
      because different surfaces shrink different ones (see below).

   Its only consumer is now the floating tab bar (AppShell), deciding whether
   to collapse out of the way — it feeds no layout math anywhere. Don't wire
   a height or shrink calculation to this hook; that's useAppViewport's job.

   The first version of this required (1) AND a measurement, and was flaky on
   the installed iOS PWA. Measuring occlusion as `window.innerHeight -
   visualViewport.height` assumes the keyboard shrinks the visual viewport and
   leaves the layout viewport alone — true in a browser tab, but a standalone
   web view gets resized for the keyboard like a native app does, so both
   shrink and the difference stays near zero. Whether the two update in the
   same frame is a race, which is what "hides sometimes" looked like. Nothing
   on the phone path measures anything now, so there is nothing left to race.

   That trade costs one case: a touch device with a hardware keyboard (an iPad
   with a Magic Keyboard) hides the bar while a field has focus even though no
   keyboard came up. It returns on blur, and being reliable on the phone the
   app is actually used on is worth more than being clever about the iPad. */

/** A shrink smaller than this is a toolbar collapsing or a rounding artefact,
    not a keyboard. The shortest iOS keyboard is a little over 200px. */
const MIN_KEYBOARD_PX = 120;

/** Input types that render as a button, a picker or a toggle — focusing one
    opens no keyboard. Everything else (text, search, email, tel, url, number,
    password, date…) does, so the list is of exceptions, not of matches. */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isTextEntry(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (element.isContentEditable) {
    return true;
  }
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(element.type);
  }
  return false;
}

/** The visual viewport in layout pixels — `height` is in visual pixels, which
    shrink under pinch-zoom — falling back to the layout viewport where the API
    is missing (jsdom, old WebKit), which reads as "never shrank". */
function visualHeight(): number {
  const viewport = window.visualViewport;
  return viewport ? viewport.height * viewport.scale : window.innerHeight;
}

export function useKeyboardVisible(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    // Live object, not a snapshot: an iPad gaining a trackpad re-evaluates.
    // Absent in jsdom, where the measured path is what the tests exercise.
    const coarsePointer = window.matchMedia?.("(pointer: coarse)");

    /* Heights as they were when focus arrived, i.e. before the keyboard could
       have opened. Recorded per focus session rather than kept as a running
       maximum, so it can't go stale across an orientation change — and *not*
       re-taken when focus hops between two fields, which would re-baseline
       with the keyboard already up and read as "nothing shrank". */
    let baseline: { visual: number; layout: number } | null = null;

    function keyboardIsUp(): boolean {
      if (coarsePointer?.matches) {
        return true;
      }
      if (!baseline) {
        return false;
      }
      // Either viewport shrinking counts: a browser tab shrinks the visual one
      // (iOS Safari, Chrome's default `interactive-widget`), while a resized
      // web view or Chrome's `resizes-content` shrinks the layout one.
      return (
        baseline.visual - visualHeight() >= MIN_KEYBOARD_PX ||
        baseline.layout - window.innerHeight >= MIN_KEYBOARD_PX
      );
    }

    function evaluate(focused: Element | null) {
      if (!isTextEntry(focused)) {
        baseline = null;
        setVisible(false);
        return;
      }
      baseline ??= { visual: visualHeight(), layout: window.innerHeight };
      setVisible(keyboardIsUp());
    }

    function handleActiveElement() {
      evaluate(document.activeElement);
    }

    function handleFocusOut(event: FocusEvent) {
      // focusout fires *before* the next element takes focus, so
      // document.activeElement is <body> mid-hop between two fields and the
      // bar would flash back in. relatedTarget is the incoming element.
      evaluate(event.relatedTarget as Element | null);
    }

    handleActiveElement();

    // focusin/focusout rather than focus/blur: those don't bubble, and the
    // element being focused is anywhere in the tree, not on a known node.
    document.addEventListener("focusin", handleActiveElement);
    document.addEventListener("focusout", handleFocusOut);
    // The keyboard animates in, so these fire repeatedly during it — fine, the
    // state only changes on the frame the boolean flips. `scroll` is in the
    // list because iOS shifts the visual viewport as well as resizing it, and
    // has been known to report the new height only on the shift.
    viewport?.addEventListener("resize", handleActiveElement);
    viewport?.addEventListener("scroll", handleActiveElement);
    window.addEventListener("resize", handleActiveElement);
    coarsePointer?.addEventListener("change", handleActiveElement);

    return () => {
      document.removeEventListener("focusin", handleActiveElement);
      document.removeEventListener("focusout", handleFocusOut);
      viewport?.removeEventListener("resize", handleActiveElement);
      viewport?.removeEventListener("scroll", handleActiveElement);
      window.removeEventListener("resize", handleActiveElement);
      coarsePointer?.removeEventListener("change", handleActiveElement);
    };
  }, []);

  return visible;
}
